import React, { useState } from 'react'
import { WeeklyEntries, TimeEntry } from '../../types'
import { timeUtils } from '../../services/timerService'
import { 
  Clock, 
  ChevronDown, 
  ChevronRight, 
  Lock,
  Edit,
  Trash2,
  StopCircle,
  MoreVertical,
  PlusCircle
} from 'lucide-react'
import Tooltip from '../common/Tooltip'

interface WeeklyTimeEntryListProps {
  weeklyEntries: WeeklyEntries
  onEdit?: (entry: TimeEntry, originalDate?: string) => void
  onDelete?: (entryId: string) => void
  onStop?: () => void
  className?: string
  disabled?: boolean
}

const WeeklyTimeEntryList: React.FC<WeeklyTimeEntryListProps> = ({
  weeklyEntries,
  onEdit,
  onDelete,
  onStop,
  className = '',
  disabled = false
}) => {
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set())

  // 將日期按倒序排列（最新的在上面）
  const sortedDates = Object.keys(weeklyEntries).sort((a, b) => new Date(b).getTime() - new Date(a).getTime())

  // 預設展開今天和昨天
  React.useEffect(() => {
    const today = new Date().toISOString().split('T')[0]
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    setExpandedDates(new Set([today, yesterday]))
  }, [])

  const toggleDateExpansion = (date: string) => {
    const newExpanded = new Set(expandedDates)
    if (newExpanded.has(date)) {
      newExpanded.delete(date)
    } else {
      newExpanded.add(date)
    }
    setExpandedDates(newExpanded)
  }

  const formatDateHeader = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
    
    // 檢查是否為今天或昨天
    if (date.toDateString() === today.toDateString()) {
      return '今天'
    } else if (date.toDateString() === yesterday.toDateString()) {
      return '昨天'
    } else {
      return date.toLocaleDateString('zh-TW', {
        month: '2-digit',
        day: '2-digit',
        weekday: 'short'
      })
    }
  }

  if (sortedDates.length === 0) {
    return (
      <div className={`${className}`}>
        <div className="text-center py-8">
          <Clock className="w-12 h-12 text-secondary-300 mx-auto mb-3" />
          <p className="text-secondary-500">本週尚無計時記錄</p>
          <p className="text-sm text-secondary-400 mt-1">
            選擇專案並開始計時來建立第一筆記錄
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {sortedDates.map((date) => {
        const dayData = weeklyEntries[date]
        const isExpanded = expandedDates.has(date)
        const isSubmitted = dayData.isSubmitted
        const canEdit = dayData.canEdit && !disabled

        return (
          <div
            key={date}
            className={`
              border rounded-lg overflow-hidden transition-all duration-200
              ${isSubmitted 
                ? 'bg-gray-50 border-gray-200' 
                : 'bg-white border-secondary-200 hover:shadow-md'
              }
            `}
          >
            {/* 日期標題 */}
            <div
              onClick={() => toggleDateExpansion(date)}
              className={`
                flex items-center justify-between p-4 cursor-pointer
                ${isSubmitted ? 'text-gray-600' : 'text-secondary-800'}
                hover:bg-secondary-50 transition-colors
              `}
            >
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-secondary-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-secondary-500" />
                  )}
                  <span className="font-medium">
                    {formatDateHeader(date)} ({date.split('-').slice(1).join('/')})
                  </span>
                </div>

                {/* 鎖定狀態指示 */}
                {isSubmitted && (
                  <Tooltip content="此日期已提交，無法編輯">
                    <div className="flex items-center space-x-1 text-gray-500">
                      <Lock className="w-4 h-4" />
                      <span className="text-sm">已提交</span>
                    </div>
                  </Tooltip>
                )}
              </div>

              {/* 總時數 */}
              <div className={`
                text-right
                ${isSubmitted ? 'text-gray-600' : 'text-secondary-600'}
              `}>
                <div className="font-mono font-semibold">
                  {timeUtils.formatDuration(dayData.totalDuration)}
                </div>
                <div className="text-xs text-secondary-500">
                  {dayData.entries.length} 筆記錄
                </div>
              </div>
            </div>

            {/* 記錄列表 */}
            {isExpanded && (
              <div className="border-t border-secondary-100">
                {dayData.entries.length === 0 ? (
                  <div className="p-4 text-center text-secondary-500">
                    此日期無記錄
                  </div>
                ) : (
                  <div className="divide-y divide-secondary-100">
                    {dayData.entries.map((entry) => (
                      <WeeklyTimeEntryItem
                        key={entry.id}
                        entry={entry}
                        onEdit={canEdit ? onEdit : undefined}
                        onDelete={canEdit ? onDelete : undefined}
                        onStop={onStop}
                        disabled={!canEdit}
                        isSubmitted={isSubmitted}
                        originalDate={date}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// 單筆週記錄項目組件
interface WeeklyTimeEntryItemProps {
  entry: TimeEntry
  onEdit?: (entry: TimeEntry, originalDate?: string) => void
  onDelete?: (entryId: string) => void
  onStop?: () => void
  disabled?: boolean
  isSubmitted?: boolean
  originalDate?: string
}

const WeeklyTimeEntryItem: React.FC<WeeklyTimeEntryItemProps> = ({
  entry,
  onEdit,
  onDelete,
  onStop,
  disabled = false,
  isSubmitted = false,
  originalDate
}) => {
  const [showActions, setShowActions] = useState(false)
  const isRunning = entry.status === 'RUNNING'
  const duration = isRunning 
    ? timeUtils.calculateDuration(entry.startTime)
    : entry.duration

  return (
    <div 
      className={`
        p-4 transition-colors
        ${isSubmitted 
          ? 'bg-gray-50' 
          : 'hover:bg-secondary-50'
        }
      `}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* 手機版佈局 */}
      <div className="block lg:hidden">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-2">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.project.color }}
            />
            <span className={`
              font-medium
              ${isSubmitted ? 'text-gray-600' : 'text-secondary-800'}
            `}>
              {entry.project.name}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className={`
              text-sm font-mono font-semibold
              ${isRunning 
                ? 'text-green-600' 
                : isSubmitted 
                  ? 'text-gray-600' 
                  : 'text-secondary-600'
              }
            `}>
              {timeUtils.formatDuration(duration)}
            </div>
            
            {!disabled && (
              <ActionButtons
                entry={entry}
                onEdit={onEdit}
                onDelete={onDelete}
                onStop={onStop}
                isSubmitted={isSubmitted}
                originalDate={originalDate}
              />
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-2 text-sm text-secondary-500 mb-2">
          <span>{timeUtils.formatTime(entry.startTime)}</span>
          <span>-</span>
          {entry.endTime ? (
            <span>{timeUtils.formatTime(entry.endTime)}</span>
          ) : (
            <span className="text-green-600 font-medium">進行中</span>
          )}
        </div>
        
        {entry.description && (
          <p className={`
            text-sm mt-2
            ${isSubmitted ? 'text-gray-600' : 'text-secondary-600'}
          `}>
            {entry.description}
          </p>
        )}
      </div>

      {/* 桌面版佈局 */}
      <div className="hidden lg:flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-1">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.project.color }}
            />
            <span className={`
              font-medium
              ${isSubmitted ? 'text-gray-600' : 'text-secondary-800'}
            `}>
              {entry.project.name}
            </span>
            
            {/* 狀態標籤 */}
            <div className="flex items-center space-x-2">
              {entry.isManual && (
                <div className="flex items-center bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium">
                  <PlusCircle className="w-3 h-3 mr-1" />
                  <span>手動</span>
                </div>
              )}
              {entry.isEdited && (
                <div className="flex items-center bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                  <Edit className="w-3 h-3 mr-1" />
                  <span>已編輯</span>
                </div>
              )}
            </div>
          </div>
          
          {entry.description && (
            <p className={`
              text-sm mb-1
              ${isSubmitted ? 'text-gray-600' : 'text-secondary-600'}
            `}>
              {entry.description}
            </p>
          )}
          
          <div className="flex items-center space-x-4 text-xs text-secondary-500">
            <span>開始: {timeUtils.formatTime(entry.startTime)}</span>
            {entry.endTime && (
              <span>結束: {timeUtils.formatTime(entry.endTime)}</span>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <div className={`
            text-lg font-mono font-semibold
            ${isRunning 
              ? 'text-green-600' 
              : isSubmitted 
                ? 'text-gray-600' 
                : 'text-secondary-600'
            }
          `}>
            {timeUtils.formatDuration(duration)}
          </div>
          
          {!disabled && (showActions || isRunning) && (
            <ActionButtons
              entry={entry}
              onEdit={onEdit}
              onDelete={onDelete}
              onStop={onStop}
              isSubmitted={isSubmitted}
              originalDate={originalDate}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// 操作按鈕組件
interface ActionButtonsProps {
  entry: TimeEntry
  onEdit?: (entry: TimeEntry, originalDate?: string) => void
  onDelete?: (entryId: string) => void
  onStop?: () => void
  isSubmitted?: boolean
  originalDate?: string
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  entry,
  onEdit,
  onDelete,
  onStop,
  isSubmitted = false,
  originalDate
}) => {
  const [showDropdown, setShowDropdown] = useState(false)
  const isRunning = entry.status === 'RUNNING'

  if (isSubmitted) return null

  return (
    <div className="flex items-center space-x-1">
      {/* 桌面版：直接顯示按鈕 */}
      <div className="hidden md:flex items-center space-x-1">
        {isRunning ? (
          onStop && (
            <Tooltip content="停止計時">
              <button
                onClick={onStop}
                className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
              >
                <StopCircle className="w-4 h-4" />
              </button>
            </Tooltip>
          )
        ) : (
          <>
            {onEdit && (
              <Tooltip content="編輯記錄">
                <button
                  onClick={() => onEdit(entry, originalDate)}
                  className="p-1.5 text-secondary-600 hover:bg-secondary-100 rounded-md transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip content="刪除記錄">
                <button
                  onClick={() => onDelete(entry.id)}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </Tooltip>
            )}
          </>
        )}
      </div>

      {/* 手機版：下拉選單 */}
      <div className="md:hidden relative">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="p-1.5 text-secondary-600 hover:bg-secondary-100 rounded-md transition-colors"
        >
          <MoreVertical className="w-4 h-4" />
        </button>

        {showDropdown && (
          <div className="absolute right-0 top-8 mt-1 w-32 bg-white rounded-lg shadow-lg border border-secondary-200 py-1 z-10">
            {isRunning ? (
              onStop && (
                <button
                  onClick={() => {
                    onStop()
                    setShowDropdown(false)
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                >
                  <StopCircle className="w-4 h-4 mr-2" />
                  停止計時
                </button>
              )
            ) : (
              <>
                {onEdit && (
                  <button
                    onClick={() => {
                      onEdit(entry, originalDate)
                      setShowDropdown(false)
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-secondary-700 hover:bg-secondary-50 flex items-center"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    編輯記錄
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => {
                      onDelete(entry.id)
                      setShowDropdown(false)
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    刪除記錄
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default WeeklyTimeEntryList
